import React, { useCallback, useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Spacer,
  VStack,
  Text,
  HStack,
  Center,
  Spinner,
  InputGroup,
  InputLeftElement,
  Input,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { BenchmarkType } from "build-ts/enums";
import { DatePeriodEnum } from "build-ts/relative_date";
import { useConfigurationContext } from "context/ConfigurationContext";
import BenchmarkCreationForm from "modules/benchmark/components/BenchmarkCreationForm";
import CustomModal from "components/modals/CustomModal";
import { AppIcons } from "constants/icons";
import ListItemMeta from "modules/common/ListItemMeta";
import { listPanelScrollSx } from "utils/listScrollAreaStyles";
import ConfigurationListFilterControl from "modules/configuration/components/ConfigurationListFilterControl";
import { useMatch } from "react-router-dom";

interface BenchmarkListProps {
  splitDetailOpen: boolean;
  // Provided by BenchmarkListContainer
  isLoading: boolean;
  isCreateModalOpen: boolean;
  onOpenCreateModal: () => void;
  onCloseCreateModal: () => void;
  onCreateBenchmark: (name: string, type: BenchmarkType, granularity?: DatePeriodEnum) => Promise<void>;
}

const BenchmarkList: React.FC<BenchmarkListProps> = ({
  splitDetailOpen,
  isLoading,
  isCreateModalOpen,
  onOpenCreateModal,
  onCloseCreateModal,
  onCreateBenchmark,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { benchmarkList, selectedBenchmark, setSelectedMetric, setSelectedKgColumn, handleEditBenchmark } =
    useConfigurationContext();
  const navigate = useNavigate();
  const [filteredBenchmarks, setFilteredBenchmarks] = useState<Header[]>([]);

  const benchmarksIdMatch = useMatch("/mkg/benchmarks/:id");
  const duplicateReloadRef = useRef<string | null>(null);

  // auto-select a benchmark when the URL contains /mkg/benchmarks/:id
  useEffect(() => {
    const id = benchmarksIdMatch?.params?.id;
    if (!id || !benchmarkList.length) return;
    if (duplicateReloadRef.current === id) return;
    const header = benchmarkList.find(h => h.id === id);
    if (!header) return;
    handleEditBenchmark(header);
    duplicateReloadRef.current = id;
  }, [benchmarkList, benchmarksIdMatch?.params?.id]);

  const handleBenchmarkClick = useCallback(
    (benchmark: Header) => {
      handleEditBenchmark(benchmark);
      setSelectedMetric(null);
      setSelectedKgColumn(null);
      navigate(`/mkg/benchmarks/${benchmark.id}`);
    },
    [handleEditBenchmark, setSelectedMetric, setSelectedKgColumn, navigate]
  );

  const hasActiveFilters = searchQuery.trim() !== "" || filteredBenchmarks.length < benchmarkList.length;

  return (
    <Box h="100%" minH={0} display="flex" flexDirection="column">
      {/* Search and actions bar */}
      <Box px={3} py={3} flexShrink={0}>
        <HStack spacing={3} alignItems="center" width="100%">
          <InputGroup flex="1" maxW="400px">
            <InputLeftElement pointerEvents="none">
              <Icon as={AppIcons.search} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search benchmarks..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              bg="white"
              size="sm"
              _focus={{
                borderColor: "purple.700",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-700)",
              }}
            />
          </InputGroup>

          <Button
            onClick={onOpenCreateModal}
            leftIcon={<Icon as={AppIcons.plus} boxSize={3} />}
            size="xs"
            aria-label="Create New Benchmark"
            borderRadius="md"
            title="Create New Benchmark"
            bg="gray.50"
            color="purple.700"
            _hover={{ bg: "purple.700", color: "white" }}
            variant="outline"
            borderWidth="2px"
            borderColor="purple.700"
            fontWeight="700"
            iconSpacing={1}
            cursor="pointer"
          >
            New
          </Button>
        </HStack>
      </Box>

      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        pl={4}
        pr={splitDetailOpen ? 6 : 4}
        pb={14}
        minHeight={0}
        sx={listPanelScrollSx(splitDetailOpen)}
      >
        {isLoading ? (
          <Center height="100%">
            <Spinner size="md" color="purple.500" />
          </Center>
        ) : (
          <VStack spacing={2} align="stretch">
            <HStack justify="space-between" align="center" mx={1} p={0}>
              <HStack spacing={2} align="center" flexShrink={0}>
                {!splitDetailOpen && <Icon as={AppIcons.benchmark} boxSize={7} color="gray.700" />}
                <Text fontSize="sm" fontWeight="700" color="gray.700" letterSpacing="tight" mb={0}>
                  Benchmarks
                </Text>
              </HStack>
              {!splitDetailOpen && <Box flex="1" minW={2} height="1px" bg="gray.300" mx={1} />}
              <ConfigurationListFilterControl
                headerList={benchmarkList}
                type="benchmark"
                searchQuery={searchQuery}
                splitDetailOpen={splitDetailOpen}
                onChange={setFilteredBenchmarks}
              />
            </HStack>
            {filteredBenchmarks.length > 0 ? (
              splitDetailOpen ? (
                filteredBenchmarks.map(benchmark => {
                  const sel = selectedBenchmark?.id === benchmark.id;
                  return (
                    <Button
                      key={benchmark.id}
                      variant="outline"
                      size="sm"
                      flexDirection="column"
                      alignItems="stretch"
                      justifyContent="flex-start"
                      borderColor={sel ? "purple.600" : "gray.200"}
                      _hover={{
                        bg: sel ? "purple.700" : "purple.50",
                        borderColor: "purple.400",
                        color: sel ? "white" : "gray.800",
                      }}
                      bg={sel ? "purple.700" : "white"}
                      color={sel ? "white" : "gray.800"}
                      borderRadius="lg"
                      fontSize="sm"
                      fontWeight={sel ? "600" : "400"}
                      height="auto"
                      minH="4.5rem"
                      py={3}
                      px={4}
                      whiteSpace="normal"
                      textAlign="left"
                      onClick={() => handleBenchmarkClick(benchmark)}
                    >
                      <Flex direction="column" align="stretch" flex={1} w="full" minH="full" minW={0}>
                        <HStack align="flex-start" spacing={2} w="full" minW={0} pt={0.5}>
                          <Icon
                            as={AppIcons.benchmark}
                            boxSize={5}
                            flexShrink={0}
                            color={sel ? "whiteAlpha.900" : "purple.600"}
                            mt={0.5}
                            aria-hidden
                          />
                          <Text
                            mb={0}
                            fontWeight={sel ? "600" : "500"}
                            noOfLines={2}
                            fontSize="md"
                            textAlign="left"
                            flex={1}
                            minW={0}
                          >
                            {benchmark.name}
                          </Text>
                        </HStack>
                        <Spacer minH={2} />
                        <Box w="full" pt={1}>
                          <ListItemMeta header={benchmark} selected={sel} />
                        </Box>
                      </Flex>
                    </Button>
                  );
                })
              ) : (
                <SimpleGrid columns={3} spacing={3} w="full">
                  {filteredBenchmarks.map(benchmark => {
                    const selected = selectedBenchmark?.id === benchmark.id;
                    return (
                      <Button
                        key={benchmark.id}
                        w="full"
                        h="auto"
                        minH="6rem"
                        py={4}
                        px={5}
                        borderRadius="2xl"
                        variant="outline"
                        borderWidth={selected ? "2px" : "1px"}
                        borderColor={selected ? "purple.600" : "gray.200"}
                        flexDirection="column"
                        alignItems="stretch"
                        justifyContent="flex-start"
                        bg={selected ? "purple.700" : "white"}
                        color={selected ? "white" : "gray.800"}
                        _hover={{
                          bg: selected ? "purple.700" : "purple.50",
                          borderColor: "purple.400",
                        }}
                        onClick={() => handleBenchmarkClick(benchmark)}
                      >
                        <Flex direction="column" align="stretch" flex={1} w="full" minH="full" minW={0}>
                          <HStack align="flex-start" spacing={2} w="full" minW={0} pt={1}>
                            <Icon
                              as={AppIcons.benchmark}
                              boxSize={5}
                              flexShrink={0}
                              color={selected ? "whiteAlpha.900" : "purple.600"}
                              mt={0.5}
                              aria-hidden
                            />
                            <Text
                              fontWeight="600"
                              fontSize="md"
                              noOfLines={2}
                              lineHeight="short"
                              textAlign="left"
                              mb={0}
                              flex={1}
                              minW={0}
                            >
                              {benchmark.name}
                            </Text>
                          </HStack>
                          <Spacer minH={2} />
                          <Box w="full" pt={1}>
                            <ListItemMeta header={benchmark} selected={selected} />
                          </Box>
                        </Flex>
                      </Button>
                    );
                  })}
                </SimpleGrid>
              )
            ) : (
              <Text color="gray.500" textAlign="center" py={8}>
                {hasActiveFilters ? "No benchmarks match your search or filters." : "No benchmarks found."}
              </Text>
            )}
          </VStack>
        )}
      </Box>

      {isCreateModalOpen && (
        <CustomModal
          isOpen={isCreateModalOpen}
          onClose={onCloseCreateModal}
          title="Create New Benchmark"
          size="sm"
          maxWidth="600px"
        >
          <BenchmarkCreationForm onClose={onCloseCreateModal} onCreateBenchmark={onCreateBenchmark} />
        </CustomModal>
      )}
    </Box>
  );
};

export default BenchmarkList;
